import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import QuickActionButton from '../../components/ui/QuickActionButton';
import TrainerCard from './components/TrainerCard';
import SearchFilters from './components/SearchFilters';
import TrainerTestimonial from './components/TrainerTestimonial';
import StatsOverview from './components/StatsOverview';
import Icon from '../../components/AppIcon';

const TrainerProfiles = () => {
  const [filters, setFilters] = useState({
    searchQuery: '',
    specialty: 'all',
    priceRange: 'all',
    experience: 'all',
    sortBy: 'rating',
    availableNow: false,
    featuredOnly: false,
    acceptingClients: false
  });

  const [showFilters, setShowFilters] = useState(false);

  const trainers = [
  {
    id: 1,
    name: "Sarah Mitchell",
    title: "Certified Personal Trainer & Nutritionist",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1290f2055-1763299401840.png",
    imageAlt: "Professional female fitness trainer with blonde hair in athletic wear holding clipboard in modern gym with equipment visible in background",
    rating: 4.9,
    reviews: 127,
    specializations: ["Strength Training", "Nutrition", "Weight Loss", "HIIT"],
    experience: 8,
    clients: 45,
    hourlyRate: 85,
    isAvailable: true,
    featured: true,
    certifications: ["NASM-CPT", "Precision Nutrition L1", "CrossFit L2"],
    philosophy: "I believe in sustainable fitness through balanced nutrition and progressive strength training. Every client deserves a personalized approach that fits their lifestyle.",
    successStory: "Helped client lose 50 lbs in 6 months while building lean muscle mass and improving overall health markers."
  },
  {
    id: 2,
    name: "Marcus Johnson",
    title: "Sports Performance Coach",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1877b239b-1766952821814.png",
    imageAlt: "Athletic African American male trainer with muscular build wearing black tank top demonstrating exercise form in well-lit fitness studio",
    rating: 4.8,
    reviews: 98,
    specializations: ["Sports Performance", "Strength Training", "Bodybuilding"],
    experience: 12,
    clients: 38,
    hourlyRate: 95,
    isAvailable: false,
    featured: true,
    certifications: ["CSCS", "USAW L1", "FMS"],
    philosophy: "Athletic performance is built on a foundation of proper movement patterns, progressive overload, and mental toughness.",
    successStory: "Trained collegiate athlete who improved vertical jump by 8 inches and secured Division 1 scholarship."
  },
  {
    id: 3,
    name: "Emily Chen",
    title: "Yoga & Wellness Specialist",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_13ac99ba1-1763299427380.png",
    imageAlt: "Serene Asian female yoga instructor with long dark hair in meditation pose wearing white athletic wear in peaceful studio with natural lighting",
    rating: 5.0,
    reviews: 156,
    specializations: ["Yoga", "Flexibility", "Mindfulness", "Rehabilitation"],
    experience: 10,
    clients: 52,
    hourlyRate: 75,
    isAvailable: true,
    featured: false,
    certifications: ["RYT-500", "Yoga Medicine", "Meditation Teacher"],
    philosophy: "True wellness comes from the harmony of mind, body, and spirit. I guide clients to discover their inner strength through mindful movement.",
    successStory: "Helped client recover from chronic back pain through targeted yoga therapy and mobility work."
  },
  {
    id: 4,
    name: "David Rodriguez",
    title: "Functional Fitness Expert",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fa9e44fc-1763295892954.png",
    imageAlt: "Confident Hispanic male trainer with short dark hair in navy blue athletic shirt demonstrating functional movement in modern gym facility",
    rating: 4.7,
    reviews: 89,
    specializations: ["Functional Training", "CrossFit", "Mobility"],
    experience: 6,
    clients: 41,
    hourlyRate: 70,
    isAvailable: true,
    featured: false,
    certifications: ["CrossFit L2", "FMS", "TRX Certified"],
    philosophy: "Fitness should translate to real-life activities. I focus on building functional strength that improves daily movement quality.",
    successStory: "Transformed sedentary office worker into completing first marathon and obstacle course race."
  },
  {
    id: 5,
    name: "Jessica Williams",
    title: "Pre/Postnatal Fitness Specialist",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1290f2055-1763299401840.png",
    imageAlt: "Warm and approachable female trainer with brown hair in comfortable athletic wear smiling while holding exercise ball in bright fitness studio",
    rating: 4.9,
    reviews: 134,
    specializations: ["Prenatal Fitness", "Postnatal Recovery", "Core Strength"],
    experience: 9,
    clients: 29,
    hourlyRate: 80,
    isAvailable: true,
    featured: true,
    certifications: ["Pre/Postnatal Certified", "Pelvic Floor Specialist", "NASM-CPT"],
    philosophy: "Supporting women through their fitness journey during pregnancy and beyond with safe, effective programming.",
    successStory: "Guided 50+ women through healthy pregnancies and successful postpartum recoveries."
  },
  {
    id: 6,
    name: "Alex Thompson",
    title: "Senior Fitness & Rehabilitation Coach",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d41cb7a7-1766583118216.png",
    imageAlt: "Experienced male trainer with gray hair and beard wearing professional athletic attire demonstrating proper form with resistance bands in rehabilitation area",
    rating: 4.8,
    reviews: 112,
    specializations: ["Senior Fitness", "Rehabilitation", "Balance Training"],
    experience: 15,
    clients: 35,
    hourlyRate: 90,
    isAvailable: false,
    featured: false,
    certifications: ["Senior Fitness Specialist", "Corrective Exercise", "Physical Therapy Aide"],
    philosophy: "Age is just a number. I help seniors maintain independence and quality of life through safe, progressive exercise.",
    successStory: "Helped 70-year-old client regain mobility after hip replacement and return to hiking."
  }];


  const testimonials = [
  {
    memberName: "Jennifer Adams",
    memberImage: "https://images.unsplash.com/photo-1675339740471-4e9e51637fc0",
    memberImageAlt: "Smiling woman with shoulder-length brown hair wearing casual athletic wear in bright indoor setting",
    rating: 5,
    program: "Weight Loss Program",
    duration: "6 months",
    review: "Sarah completely transformed my approach to fitness and nutrition. Her personalized program helped me lose 35 pounds while building strength I never thought possible. She's not just a trainer, she's a life coach!",
    weightLost: "35 lbs",
    strengthGain: "+40%"
  },
  {
    memberName: "Michael Torres",
    memberImage: "https://img.rocket.new/generatedImages/rocket_gen_img_187b350ba-1763294180584.png",
    memberImageAlt: "Athletic man with short dark hair in sports jersey smiling confidently in gym environment",
    rating: 5,
    program: "Sports Performance",
    duration: "4 months",
    review: "Marcus took my athletic performance to the next level. His expertise in sports-specific training helped me improve my speed, power, and agility. I'm now performing better than ever in my sport.",
    weightLost: "8 lbs",
    strengthGain: "+55%"
  },
  {
    memberName: "Lisa Patel",
    memberImage: "https://images.unsplash.com/photo-1625402649534-35c2fd471d91",
    memberImageAlt: "Peaceful woman with long dark hair in meditation pose wearing white yoga attire in serene studio",
    rating: 5,
    program: "Yoga & Wellness",
    duration: "8 months",
    review: "Emily's yoga classes have been life-changing. I came to her with chronic back pain and stress. Through her guidance, I've found relief, flexibility, and inner peace. She truly cares about her clients' wellbeing.",
    weightLost: "12 lbs",
    strengthGain: "+30%"
  }];


  const stats = {
    totalTrainers: 24,
    avgRating: "4.8",
    sessionsToday: 47,
    specialties: 12
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      searchQuery: '',
      specialty: 'all',
      priceRange: 'all',
      experience: 'all',
      sortBy: 'rating',
      availableNow: false,
      featuredOnly: false,
      acceptingClients: false
    });
  };

  const filteredTrainers = useMemo(() => {
    let result = [...trainers];

    if (filters?.searchQuery) {
      result = result?.filter((trainer) =>
      trainer?.name?.toLowerCase()?.includes(filters?.searchQuery?.toLowerCase())
      );
    }

    if (filters?.specialty !== 'all') {
      result = result?.filter((trainer) =>
      trainer?.specializations?.some((spec) =>
      spec?.toLowerCase()?.includes(filters?.specialty?.toLowerCase())
      )
      );
    }

    if (filters?.priceRange !== 'all') {
      const [min, max] = filters?.priceRange?.split('-')?.map((v) => v?.replace('+', ''));
      result = result?.filter((trainer) => {
        if (max) {
          return trainer?.hourlyRate >= parseInt(min) && trainer?.hourlyRate <= parseInt(max);
        }
        return trainer?.hourlyRate >= parseInt(min);
      });
    }

    if (filters?.experience !== 'all') {
      const [min, max] = filters?.experience?.split('-')?.map((v) => v?.replace('+', ''));
      result = result?.filter((trainer) => {
        if (max) {
          return trainer?.experience >= parseInt(min) && trainer?.experience <= parseInt(max);
        }
        return trainer?.experience >= parseInt(min);
      });
    }

    if (filters?.availableNow) {
      result = result?.filter((trainer) => trainer?.isAvailable);
    }

    if (filters?.featuredOnly) {
      result = result?.filter((trainer) => trainer?.featured);
    }

    switch (filters?.sortBy) {
      case 'rating':
        result?.sort((a, b) => b?.rating - a?.rating);
        break;
      case 'experience':
        result?.sort((a, b) => b?.experience - a?.experience);
        break;
      case 'price-low':
        result?.sort((a, b) => a?.hourlyRate - b?.hourlyRate);
        break;
      case 'price-high':
        result?.sort((a, b) => b?.hourlyRate - a?.hourlyRate);
        break;
      case 'clients':
        result?.sort((a, b) => b?.clients - a?.clients);
        break;
      default:
        break;
    }

    return result;
  }, [trainers, filters]);

  return (
    <>
      <Helmet>
        <title>Trainer Profiles - FitWellness</title>
        <meta name="description" content="Discover qualified fitness professionals and book personalized training sessions at FitWellness" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="mx-auto max-w-[1440px] px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10 pb-20 lg:pb-10">
          <div className="mb-6 md:mb-8">
            <div className="flex items-center justify-between mb-2">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground">
                Meet Our Trainers
              </h1>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="lg:hidden flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg transition-smooth hover-lift press-scale">

                <Icon name="Filter" size={20} />
                <span className="text-sm font-medium caption">Filters</span>
              </button>
            </div>
            <p className="text-sm md:text-base text-muted-foreground">
              Connect with certified fitness professionals who will help you achieve your goals
            </p>
          </div>

          <div className="mb-6 md:mb-8">
            <StatsOverview stats={stats} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 md:gap-8">
            <div className={`lg:col-span-1 ${showFilters ? 'block' : 'hidden lg:block'}`}>
              <div className="sticky top-24">
                <SearchFilters
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onClearFilters={handleClearFilters} />

              </div>
            </div>

            <div className="lg:col-span-3 space-y-6 md:space-y-8">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg md:text-xl font-heading font-bold text-foreground">
                    Available Trainers ({filteredTrainers?.length})
                  </h2>
                </div>

                {filteredTrainers?.length === 0 ?
                <div className="bg-card border border-border rounded-xl shadow-md p-8 md:p-12 text-center">
                    <div className="flex justify-center mb-4">
                      <div className="flex items-center justify-center w-16 h-16 bg-muted rounded-full">
                        <Icon name="Search" size={32} color="var(--color-muted-foreground)" />
                      </div>
                    </div>
                    <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
                      No Trainers Found
                    </h3>
                    <p className="text-muted-foreground mb-6">
                      Try adjusting your filters to see more results
                    </p>
                    <button
                    onClick={handleClearFilters}
                    className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium transition-smooth hover-lift press-scale">

                      Clear All Filters
                    </button>
                  </div> :

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    {filteredTrainers?.map((trainer) =>
                  <TrainerCard key={trainer?.id} trainer={trainer} />
                  )}
                  </div>
                }
              </div>

              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="MessageSquare" size={24} color="var(--color-primary)" />
                  <h2 className="text-lg md:text-xl font-heading font-bold text-foreground">
                    Success Stories
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {testimonials?.map((testimonial, index) =>
                  <TrainerTestimonial key={index} testimonial={testimonial} />
                  )}
                </div>
              </div>

              <div className="bg-gradient-to-r from-primary/10 to-accent/10 border border-border rounded-xl shadow-md p-6 md:p-8">
                <div className="flex flex-col md:flex-row items-center gap-6">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-primary rounded-full">
                      <Icon name="Users" size={32} color="var(--color-primary-foreground)" />
                    </div>
                  </div>
                  <div className="flex-1 text-center md:text-left">
                    <h3 className="text-xl md:text-2xl font-heading font-bold text-foreground mb-2">
                      Can't Find the Right Trainer?
                    </h3>
                    <p className="text-sm md:text-base text-muted-foreground mb-4">
                      Our team can help match you with the perfect fitness professional based on your goals and preferences
                    </p>
                    <button className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium transition-smooth hover-lift press-scale">
                      Request Trainer Match
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        <MobileBottomNav />
        <QuickActionButton />
      </div>
    </>);

};

export default TrainerProfiles;